import * as React from 'react';
import { Button, Field, Input, MessageBar, MessageBarBody, Select, Spinner } from '@fluentui/react-components';
import { CheckmarkCircleRegular, InfoRegular, SparkleRegular } from '@fluentui/react-icons';
import type { IQueueItem } from '../../api/src/queue';
import { decide } from '../../api/src/filing/decide';
import { clientsWithAliases, type ILearnedRule } from '../../api/src/filing/learned';
import { normalizeName, type IClient } from '../../api/src/filing/clients';
import { type IFilingFields, render } from '../../api/src/filing/template';
import { docTypeById } from '../../api/src/filing/vocabulary';
import { ACCOUNTING_CA, deadlinesFor, deadlineWords, fieldsOf, getPack, type IPack, type IPackField } from '../../api/src/packs';
import { extraProblems } from '../filing/extraFacts';
import { summarizeBatches, UndoEngine } from '../../../src/engine/UndoEngine';
import { GraphService } from '../../../src/services/GraphService';
import type { IBatchResult } from '../../../src/models/Operation';
import { putQueue, removeFromQueue } from '../api';
import { errorText, useApp } from '../app/context';
import { fileDocuments, type IFileRequest } from '../filing/inbox';
import { ensureInbox, getItem, type IItem, thumbnailUrl } from '../graphx';
import { extraById, filedRecords, type IFiledRecord, recordRetention } from '../retention/retention';

interface IFields {
  client: string;
  docType: string;
  date: string;
  detail: string;
  /** AI-designed structures: the folder and a title for the name. */
  folder?: string;
  title?: string;
  /** The pack's extra facts for the type (immigration: applicant, expiry date…), by field id. */
  extra?: { [fieldId: string]: string };
}

function safeTitle(t: string): string {
  return t.replace(/["*:<>?/\\|#%]/g, ' ').replace(/\s+/g, ' ').trim().replace(/^[_~.]+/, '').slice(0, 80).replace(/[. ]+$/, '');
}

/** Name and folder in an AI-designed structure, from what the reviewer chose. */
export function renderFolderMode(f: IFields, q: IQueueItem, naming: 'dateTitle' | 'compact' | 'keep'): { name?: string; folders?: string[]; missing: string[] } {
  const missing: string[] = [];
  if (!f.folder) { missing.push('folder'); }
  const clear: string = safeTitle(f.title ?? '');
  const title: string = naming === 'compact' ? clear.split(' ').map(x => x.charAt(0).toUpperCase() + x.slice(1)).join('') : clear;
  let name: string | undefined = q.fileName;
  if (naming !== 'keep') {
    if (!title) {
      missing.push('title');
      name = undefined;
    } else {
      const base: string = /^\d{4}-\d{2}-\d{2}$/.test(f.date) ? `${f.date}_${title}` : title;
      name = q.extension ? `${base}.${q.extension}` : base;
    }
  }
  return missing.length ? { missing } : { name, folders: f.folder!.split('/'), missing };
}

/** Starting values for the form: what was read, matched and tidied exactly as automatic filing would. */
export function initialFields(q: IQueueItem, clients: IClient[], learned: ILearnedRule[], threshold: number, folderMode: boolean = false, pack: IPack = ACCOUNTING_CA): IFields {
  if (folderMode) {
    return { client: '', docType: '', detail: '', date: q.extraction.date ?? '', folder: q.extraction.folder ?? '', title: q.extraction.title ?? q.fileName.replace(/\.[^.]+$/, '') };
  }
  const d = decide(q.extraction, q.extension, clientsWithAliases(clients, learned), threshold, undefined, pack);
  return {
    client: d.fields.clientShort ?? '', docType: d.fields.docType ?? '', date: d.fields.date ?? q.extraction.date ?? '', detail: d.fields.detail ?? '',
    extra: { ...q.extraction.extra, ...extraById(d.extra) }
  };
}

function sureText(n: number | undefined, found: boolean): { text: string; cls: string } {
  if (!found || n === undefined) {
    return { text: 'not found', cls: 'sure none' };
  }
  const p: number = Math.round(n * 100);
  return { text: `${p}% sure`, cls: n >= 0.85 ? 'sure' : 'sure low' };
}

/** "Want me to remember this?" — a plain rule proposed from what the reviewer just confirmed. */
export function proposeRule(q: IQueueItem, f: IFields, clients: IClient[], learned: ILearnedRule[], user: string, pack: IPack = ACCOUNTING_CA): ILearnedRule | undefined {
  const client: IClient | undefined = clients.filter(c => c.short === f.client)[0];
  const written: string | undefined = q.extraction.client;
  const now: string = new Date().toISOString();
  const id: string = `r${Date.now().toString(36)}`;
  const source: string = `From your fix to ${q.fileName}`;
  if (client && written) {
    const names: string[] = [client.name, ...(client.aliases ?? [])].map(normalizeName);
    const known: boolean = learned.some(r => r.kind === 'alias' && normalizeName(r.written ?? '') === normalizeName(written));
    if (names.indexOf(normalizeName(written)) === -1 && !known) {
      return { id, kind: 'alias', written, client: client.short, source, createdAt: now, createdBy: user, used: 0 };
    }
  }
  const issuer: string | undefined = q.extraction.issuer;
  const sameAsClient: boolean = !!issuer && !!client && normalizeName(issuer) === normalizeName(client.name);
  if (issuer && client && !sameAsClient && docTypeById(f.docType, pack) && f.docType !== 'Other'
    && (f.docType !== q.extraction.docType || (q.extraction.docTypeConfidence ?? 0) < 0.85)
    && !learned.some(r => r.kind === 'issuer' && normalizeName(r.issuer ?? '') === normalizeName(issuer) && r.client === client.short)) {
    return { id, kind: 'issuer', issuer, docType: f.docType, client: client.short, source, createdAt: now, createdBy: user, used: 0 };
  }
  return undefined;
}

export default function Review(): React.ReactElement {
  const app = useApp();
  const w = app.workspace;
  const folderMode: boolean = w.mode === 'folders';
  const pack: IPack = getPack(w.packId);
  const Entity: string = pack.entity.one.charAt(0).toUpperCase() + pack.entity.one.slice(1);
  const waiting: IQueueItem[] = app.queue.filter(q => q.status === 'waiting');
  const skipped: IQueueItem[] = app.queue.filter(q => q.status === 'skipped');
  const [selectedId, setSelectedId] = React.useState<string | undefined>(waiting[0]?.itemId);
  const current: IQueueItem | undefined = waiting.filter(q => q.itemId === selectedId)[0] ?? waiting[0];
  const [fields, setFields] = React.useState<IFields | undefined>(undefined);
  const [file, setFile] = React.useState<IItem | null | undefined>(undefined);
  const [thumb, setThumb] = React.useState<string | undefined>(undefined);
  const [working, setWorking] = React.useState<boolean>(false);
  const [error, setError] = React.useState<string | undefined>(undefined);
  const [ask, setAsk] = React.useState<ILearnedRule | undefined>(undefined);
  // Filing worked but how long to keep the records couldn't be written on them (law pack).
  const [retentionProblem, setRetentionProblem] = React.useState<string | undefined>(undefined);

  React.useEffect(() => {
    setFields(current ? initialFields(current, w.clients, w.learned, w.threshold, folderMode, pack) : undefined);
    setFile(undefined);
    setThumb(undefined);
    setError(undefined);
    if (!current) {
      return undefined;
    }
    let cancelled: boolean = false;
    getItem(app.graph, w.driveId, current.itemId).then(async item => {
      if (cancelled) {
        return;
      }
      setFile(item ?? null);
      if (item) {
        const url: string | undefined = await thumbnailUrl(app.graph, w.driveId, item.id);
        if (!cancelled) {
          setThumb(url);
        }
      }
    }).catch(() => !cancelled && setFile(null));
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [current?.itemId]);

  const client: IClient | undefined = w.clients.filter(c => c.short === fields?.client)[0];
  const renderFields: IFilingFields = {
    date: fields?.date, clientShort: client?.short, clientName: client?.name, docType: fields?.docType || undefined,
    detail: fields?.detail || undefined, year: current?.extraction.year,
    clientFolder: client?.number ? `${client.number} - ${client.name}` : undefined, area: client?.area,
    applicant: fields?.extra?.applicant?.trim() || undefined
  };
  const extraFields: IPackField[] = folderMode ? [] : fieldsOf(pack, fields?.docType);
  const stillNeeded: string[] = fields && !folderMode ? extraProblems(fields, pack) : [];
  // A date that starts a deadline is confirmed document by document, never for a whole group.
  const confirmEach: boolean = extraFields.some(f => f.confirm);
  // The firm's own records (law: trust bank statements…) belong to no client or matter.
  const organisationRecord: boolean = docTypeById(fields?.docType, pack)?.scope === 'organisation';
  const rendered = folderMode && fields && current ? renderFolderMode(fields, current, w.naming) : render(renderFields, current?.extension ?? '', w.rules, pack);

  // Documents from the same sender, waiting too: approve them together with the same client and type.
  const issuerKey: string = normalizeName(current?.extraction.issuer ?? '');
  const group: IQueueItem[] = issuerKey && !folderMode ? waiting.filter(q => normalizeName(q.extraction.issuer ?? '') === issuerKey) : [];

  const approve = async (items: IQueueItem[]): Promise<void> => {
    if (!fields || (!client && !folderMode && !organisationRecord)) {
      return;
    }
    setWorking(true);
    setError(undefined);
    setRetentionProblem(undefined);
    try {
      const inbox: IItem = await ensureInbox(app.graph, w.driveId, w.inboxName, w.inboxFolderId);
      const requests: IFileRequest[] = [];
      const records: IFiledRecord[] = [];
      for (const q of items) {
        const f: IFields = q.itemId === current!.itemId ? fields : { ...initialFields(q, w.clients, w.learned, w.threshold, false, pack), client: fields.client, docType: fields.docType };
        // Same client and type for the whole group; each keeps its own date and detail.
        const r = folderMode ? renderFolderMode(fields, q, w.naming)
          : render({ ...renderFields, date: f.date, detail: f.detail || undefined, year: q.extraction.year, applicant: f.extra?.applicant?.trim() || undefined }, q.extension, w.rules, pack);
        const item: IItem | undefined = await getItem(app.graph, w.driveId, q.itemId);
        if (!item || !r.name || !r.folders) {
          continue;
        }
        requests.push({ file: item, parentPath: r.folders.join('/'), newName: r.name, reason: 'Checked and approved in the review queue.', confidence: 1 });
        records.push({ itemId: item.id, docType: f.docType || undefined, date: f.date || undefined, extra: folderMode ? undefined : f.extra });
      }
      const { batch, problems } = await fileDocuments({ graph: app.graph, workspace: w, audit: app.audit, user: app.account.username }, { id: inbox.id, path: inbox.name }, requests);
      const done: string[] = requests.map(r => r.file.id).filter(id => !problems[id]);
      if (Object.keys(problems).length) {
        setError(Object.values(problems)[0]);
      }
      if (done.length) {
        // Law (and other regulated packs): record category and keep-until date on each filed document.
        const retention = await recordRetention(app.graph, w, filedRecords(records, problems));
        setRetentionProblem(retention?.problem);
        await removeFromQueue(w.driveId, done);
        await app.reloadQueue();
        setAsk(folderMode ? undefined : proposeRule(current!, fields, w.clients, w.learned, app.account.username, pack));
        const next: IQueueItem | undefined = waiting.filter(q => done.indexOf(q.itemId) === -1)[0];
        setSelectedId(next?.itemId);
        app.toast({
          text: done.length === 1 ? `Filed as ${requests.filter(r => r.file.id === done[0])[0].newName}` : `Filed ${done.length} documents.`,
          action: batch ? { label: 'Undo', run: () => { void undo(batch); } } : undefined
        });
      }
    } catch (e) {
      setError(errorText(e));
    } finally {
      setWorking(false);
    }
  };

  const undo = async (batch: IBatchResult): Promise<void> => {
    try {
      const summary = summarizeBatches(await app.audit.readEntries(w.driveId)).filter(b => b.batchId === batch.batchId)[0];
      if (!summary) {
        return;
      }
      const engine = new UndoEngine({ writer: new GraphService(app.graph).createDriveWriter(w.driveId), audit: app.audit, libraryId: w.driveId, libraryName: w.libraryName, user: app.account.username, records: app.records.writer });
      await engine.undo(summary);
      app.toast({ text: 'Undone. The documents are back in Inbox; check Inbox again to see them here.' });
    } catch (e) {
      app.toast({ text: `Couldn't undo: ${errorText(e)} You can undo from History.` });
    }
  };

  const leave = async (q: IQueueItem, status: 'skipped' | 'waiting'): Promise<void> => {
    try {
      await putQueue(w.driveId, [{ ...q, status }]);
      await app.reloadQueue();
      if (status === 'skipped') {
        setSelectedId(waiting.filter(x => x.itemId !== q.itemId)[0]?.itemId);
      }
    } catch (e) {
      setError(errorText(e));
    }
  };

  const remember = async (rule: ILearnedRule): Promise<void> => {
    try {
      await app.saveWorkspace({ ...app.workspace, learned: [...app.workspace.learned, rule] });
      setAsk(undefined);
      app.toast({ text: 'Saved. It will be handled on its own next time.' });
    } catch (e) {
      setError(errorText(e));
    }
  };

  const x = current?.extraction;
  const typeLabel = (id?: string): string => docTypeById(id, pack)?.label ?? '';

  return (
    <div className="review">
      <section className="queue" aria-label="Files waiting">
        <div style={{ padding: '0 6px 8px' }}>
          <h1 style={{ fontSize: 22 }}>Review queue</h1>
          <span className="small" style={{ fontSize: 14 }}>{waiting.length === 0 ? 'Nothing waiting' : `${waiting.length} file${waiting.length === 1 ? ' needs' : 's need'} you`}</span>
        </div>
        {waiting.map(q => (
          <button key={q.itemId} type="button" className="queue-item" aria-current={q.itemId === current?.itemId} onClick={() => setSelectedId(q.itemId)}>
            <span className="file">{q.fileName}</span>
            <span className="small">{q.reasons[0] ?? 'Needs a quick check.'}</span>
          </button>
        ))}
        {skipped.length > 0 && (
          <details style={{ padding: '10px 6px' }}>
            <summary className="small">Left in Inbox ({skipped.length})</summary>
            {skipped.map(q => (
              <div key={q.itemId} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8, padding: '6px 0' }}>
                <span className="mono small" style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{q.fileName}</span>
                <Button size="small" onClick={() => { void leave(q, 'waiting'); }}>Review</Button>
              </div>
            ))}
          </details>
        )}
      </section>

      <main className="review-main">
        {ask && (
          <div className="learn" role="status">
            <SparkleRegular fontSize={20} color="var(--blue)" />
            <span style={{ flexGrow: 1, fontSize: 15 }}>
              <strong>Want me to remember this?</strong>{' '}
              {ask.kind === 'alias'
                ? `Always treat "${ask.written}" as ${w.clients.filter(c => c.short === ask.client)[0]?.name}?`
                : `Always file ${ask.issuer} documents as "${typeLabel(ask.docType)}" for ${w.clients.filter(c => c.short === ask.client)[0]?.name}?`}
            </span>
            <Button appearance="primary" onClick={() => { void remember(ask); }}>Yes, always</Button>
            <Button onClick={() => setAsk(undefined)}>Just this once</Button>
          </div>
        )}
        {retentionProblem && <MessageBar intent="warning"><MessageBarBody>{retentionProblem}</MessageBarBody></MessageBar>}
        {error && <MessageBar intent="error"><MessageBarBody>{error}</MessageBarBody></MessageBar>}

        {!current && (
          <div style={{ flexGrow: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 12, textAlign: 'center', padding: 40 }}>
            <CheckmarkCircleRegular fontSize={56} color="var(--green)" />
            <h2 style={{ fontSize: 24 }}>All caught up</h2>
            <p className="small" style={{ fontSize: 15, maxWidth: 420 }}>Every file in the queue has been handled. New files that need you will appear here.</p>
            <a href="#/rules">See what I've learned from your fixes</a>
          </div>
        )}

        {current && fields && (
          <>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              <span className="mono" style={{ fontSize: 20, fontWeight: 600, wordBreak: 'break-all' }}>{current.fileName}</span>
              <div className="note" role="note"><InfoRegular fontSize={18} />{current.reasons.join(' ') || 'Needs a quick check.'}</div>
            </div>
            {file === null && (
              <MessageBar intent="warning"><MessageBarBody>
                This file isn't in Inbox any more. It may have been moved or deleted in SharePoint.{' '}
                <Button size="small" onClick={() => { void removeFromQueue(w.driveId, [current.itemId]).then(app.reloadQueue); }}>Remove from the queue</Button>
              </MessageBarBody></MessageBar>
            )}
            <div style={{ display: 'flex', gap: 28, flexWrap: 'wrap' }}>
              <figure className="preview" style={{ margin: 0 }} aria-label="Document preview">
                <div className="preview-page">
                  {file === undefined ? <Spinner size="small" /> : thumb ? <img src={thumb} alt={`First page of ${current.fileName}`} /> : <span className="small" style={{ padding: 20 }}>No preview for this file.</span>}
                </div>
                <figcaption className="small">Page 1 · {file?.webUrl ? <a href={file.webUrl} target="_blank" rel="noreferrer">Open in SharePoint</a> : null}</figcaption>
              </figure>

              <div style={{ flexGrow: 1, minWidth: 300, display: 'flex', flexDirection: 'column', gap: 16 }}>
                {folderMode ? (
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 16 }}>
                    <Field label={<>Folder <span className={sureText(x?.folderConfidence, !!x?.folder).cls}>{sureText(x?.folderConfidence, !!x?.folder).text}</span></>}
                      hint={w.folderMap.filter(f => f.path === fields.folder)[0]?.purpose}>
                      <Select value={fields.folder ?? ''} onChange={(_, d) => setFields({ ...fields, folder: d.value })}>
                        <option value="">Choose a folder…</option>
                        {w.folderMap.map(f => <option key={f.path} value={f.path}>{f.path.split('/').join(' › ')}</option>)}
                      </Select>
                    </Field>
                    {w.naming !== 'keep' && (
                      <Field label="Title">
                        <Input value={fields.title ?? ''} placeholder="e.g. Vacation Policy" onChange={(_, d) => setFields({ ...fields, title: d.value })} />
                      </Field>
                    )}
                    {w.naming !== 'keep' && (
                      <Field label={<>Date (optional) <span className={sureText(x?.dateConfidence, !!x?.date).cls}>{sureText(x?.dateConfidence, !!x?.date).text}</span></>}
                        validationMessage={fields.date && !/^\d{4}-\d{2}-\d{2}$/.test(fields.date) ? 'Use YYYY-MM-DD, or leave it empty' : undefined}>
                        <Input value={fields.date} placeholder="YYYY-MM-DD" onChange={(_, d) => setFields({ ...fields, date: d.value })} />
                      </Field>
                    )}
                  </div>
                ) : (
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 16 }}>
                  <Field label={<>{Entity} <span className={sureText(x?.clientConfidence, !!x?.client).cls}>{sureText(x?.clientConfidence, !!x?.client).text}</span></>}
                    hint={x?.client && client?.name !== x.client ? `Written as "${x.client}"` : undefined}>
                    <Select value={fields.client} onChange={(_, d) => setFields({ ...fields, client: d.value })}>
                      <option value="">Choose a client…</option>
                      {w.clients.map(c => <option key={c.short} value={c.short}>{c.name}</option>)}
                    </Select>
                  </Field>
                  <Field label={<>Document type <span className={sureText(x?.docTypeConfidence, !!x?.docType).cls}>{sureText(x?.docTypeConfidence, !!x?.docType).text}</span></>}>
                    <Select value={fields.docType} onChange={(_, d) => setFields({ ...fields, docType: d.value })}>
                      <option value="">Choose a type…</option>
                      {pack.docTypes.map(t => <option key={t.id} value={t.id}>{t.label}</option>)}
                    </Select>
                  </Field>
                  <Field label={<>Document date <span className={sureText(x?.dateConfidence, !!x?.date).cls}>{sureText(x?.dateConfidence, !!x?.date).text}</span></>}
                    validationMessage={fields.date && !/^\d{4}-\d{2}-\d{2}$/.test(fields.date) ? 'Use YYYY-MM-DD, e.g. 2025-03-31' : undefined}>
                    <Input value={fields.date} placeholder="YYYY-MM-DD" onChange={(_, d) => setFields({ ...fields, date: d.value })} />
                  </Field>
                  <Field label="Detail (bank, vendor, period)" hint={docTypeById(fields.docType, pack)?.detailMeaning}>
                    <Input value={fields.detail} placeholder="e.g. RBC, Q2, Staples" onChange={(_, d) => setFields({ ...fields, detail: d.value })} />
                  </Field>
                  {extraFields.map(p => (
                    <Field key={p.id} label={p.confirm ? `${p.label} (please check)` : p.label} hint={p.confirm ? 'Starts a deadline: check it against the document.' : undefined}
                      validationMessage={p.kind === 'date' && fields.extra?.[p.id] && !/^\d{4}-\d{2}-\d{2}$/.test(fields.extra[p.id]) ? 'Use YYYY-MM-DD, e.g. 2026-09-15' : undefined}>
                      {p.choices ? (
                        <Select value={fields.extra?.[p.id] ?? ''} onChange={(_, d) => setFields({ ...fields, extra: { ...fields.extra, [p.id]: d.value } })}>
                          <option value="">Choose…</option>
                          {p.choices.map(c => <option key={c} value={c}>{c}</option>)}
                        </Select>
                      ) : (
                        <Input value={fields.extra?.[p.id] ?? ''} placeholder={p.kind === 'date' ? 'YYYY-MM-DD' : undefined}
                          onChange={(_, d) => setFields({ ...fields, extra: { ...fields.extra, [p.id]: d.value } })} />
                      )}
                    </Field>
                  ))}
                </div>
                )}

                {/* A deadline the confirmed date starts: shown for the person to check, written on the document once approved, never calendared. */}
                {!folderMode && fields && deadlinesFor(pack, fields.docType, fields.extra).map(d => (
                  <MessageBar key={d.deadline.id} intent="warning">
                    <MessageBarBody>{deadlineWords(d)}</MessageBarBody>
                  </MessageBar>
                ))}

                <div className="result-box">
                  <span className="small" style={{ fontWeight: 600 }}>It will be named</span>
                  <span className="result-name" style={{ color: rendered.name && !stillNeeded.length ? 'var(--green-dark)' : 'var(--amber)' }}>
                    {rendered.name && !stillNeeded.length ? rendered.name : `Still needed: ${[...rendered.missing, ...stillNeeded].join(', ')}`}
                  </span>
                  <span className="small" style={{ fontWeight: 600, marginTop: 6 }}>And filed in</span>
                  <span>{rendered.folders ? `${w.libraryName} › ${rendered.folders.join(' › ')}` : '…'}</span>
                </div>

                <div style={{ display: 'flex', gap: 12, alignItems: 'center', flexWrap: 'wrap' }}>
                  <Button appearance="primary" disabled={working || !rendered.name || stillNeeded.length > 0 || file === null} onClick={() => { void approve([current]); }}>
                    {working ? 'Filing…' : 'Approve & file'}
                  </Button>
                  {group.length > 1 && rendered.name && !confirmEach && !stillNeeded.length && (
                    <Button disabled={working || file === null} onClick={() => { void approve(group); }}>Approve all {group.length} from {current.extraction.issuer}</Button>
                  )}
                  <Button disabled={working} onClick={() => { void leave(current, 'skipped'); }}>Leave in Inbox</Button>
                </div>
                <span className="small">Nothing moves until you approve. Undo any time from History.</span>
              </div>
            </div>
          </>
        )}
      </main>
    </div>
  );
}
